import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { useIntl } from 'gatsby-plugin-intl';
import { useLocation } from '@gatsbyjs/reach-router';

const SEO = ({ description, lang, meta, title, image }) => {
  const locale = useIntl().locale;
  const { pathname } = useLocation();
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            host
          }
        }
      }
    `,
  );

  const host = site.siteMetadata.host;
  const metaDescription = description || site.siteMetadata.description;
  // Canonical/hreflang URLs must use the ".../index.html" form: on S3/CloudFront
  // only exact object keys resolve, so "/de/about/" returns a 404 while
  // "/de/about/index.html" is the real page. Every page is also built as a
  // trailing-slash variant that renders into the same HTML file, so both
  // variants have to emit identical URLs here.
  let basePath = pathname.replace(/^\/(de|en)(?=\/|$)/, '');
  if (!basePath.endsWith('.html')) {
    basePath = `${basePath.replace(/\/$/, '')}/index.html`;
  }

  return (
    <Helmet
      htmlAttributes={{
        lang: lang || locale,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.author}`}
      link={[
        { rel: 'canonical', href: `${host}/${locale}${basePath}` },
        { rel: 'alternate', hreflang: 'de', href: `${host}/de${basePath}` },
        { rel: 'alternate', hreflang: 'en', href: `${host}/en${basePath}` },
        { rel: 'alternate', hreflang: 'x-default', href: `${host}/de${basePath}` },
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: `${title} | ${site.siteMetadata.author}`,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:image`,
          content: image ? `${host}${image}` : `${host}/img/logo-white.png`,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:locale`,
          content: locale === 'de' ? 'de_DE' : 'en_US',
        },
        {
          property: `og:locale:alternate`,
          content: locale === 'de' ? 'en_US' : 'de_DE',
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: `${title} | ${site.siteMetadata.author}`,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: image ? `${host}${image}` : `${host}/img/logo-white.png`,
        },
      ].concat(meta)}
    />
  );
};

SEO.defaultProps = {
  lang: '',
  meta: [],
  description: '',
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
};

export default SEO;
